/**
 * LookSections.jsx - Customize > Appearance, Rows & totals and Sharing
 * (round 4, 10/9/26). Every control changes the table behind the panel at
 * once and is saved with the view (viewConfig.js `look`, `hover`, `sort`).
 */
import { Button } from '../../ui';
import { DEFAULT_HOVER, HIGHLIGHT_COLORS } from '../highlights';
import { BASE_FONTS } from '../viewConfig';
import { Switch, Segmented, ColorChoice, SectionHead, Row } from './controls';

const SELECT = 'border border-border-strong rounded px-2 py-1 text-[12px] bg-surface';

export function AppearanceSection({ view }) {
  const l = view.look;
  return (
    <div>
      <SectionHead
        title="Appearance"
        hint="How the table looks. Everything here changes the table behind this panel straight away."
        onReset={() => view.resetSection('appearance')}
        resetLabel="Reset appearance"
      />
      <Row label="Row height" hint="How much room each row gets.">
        <Segmented
          label="Row height"
          value={l.density}
          onChange={(density) => view.setLook({ density })}
          options={[
            ['compact', 'Compact'],
            ['comfortable', 'Comfortable'],
            ['spacious', 'Spacious'],
          ]}
        />
      </Row>
      <Row label="Text size" hint="The table's own size; a column can still have its own (Columns ⋯).">
        <select
          aria-label="Table text size"
          className={SELECT}
          value={l.fontSize || ''}
          onChange={(e) => view.setLook({ fontSize: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">Standard (10.5px)</option>
          {BASE_FONTS.filter((n) => n !== 10.5).map((n) => (
            <option key={n} value={n}>
              {n}px
            </option>
          ))}
        </select>
      </Row>
      <Row label="Striped rows" hint="Every other row lightly shaded.">
        <Switch on={l.zebra} onChange={(zebra) => view.setLook({ zebra })} label="Striped rows" />
      </Row>
      <Row label="Grid lines" hint="Thin lines between the columns.">
        <Switch on={l.gridlines} onChange={(gridlines) => view.setLook({ gridlines })} label="Grid lines" />
      </Row>
      <Row label="Header style">
        <Segmented
          label="Header style"
          value={l.header}
          onChange={(header) => view.setLook({ header })}
          options={[
            ['band', 'Green band'],
            ['light', 'Light'],
          ]}
        />
      </Row>
      <Row label="Long text" hint="Wrap onto more lines, or cut off with … (hover to read it all).">
        <Segmented
          label="Long text"
          value={l.wrap ? 'wrap' : 'cut'}
          onChange={(v) => view.setLook({ wrap: v === 'wrap' })}
          options={[
            ['wrap', 'Wrap'],
            ['cut', 'Cut off …'],
          ]}
        />
      </Row>
      <Row label="Row hover" hint="Light up the whole row under the mouse.">
        <Switch on={view.hover.on !== false} onChange={(on) => view.setHover({ on })} label="Highlight the whole row under the mouse" />
      </Row>
      {view.hover.on !== false && (
        <div className="py-2">
          <ColorChoice
            label="Row hover colour"
            presets={{ mint: { label: 'mint (default)', fill: DEFAULT_HOVER.color }, ...HIGHLIGHT_COLORS }}
            value={view.hover.color === DEFAULT_HOVER.color ? 'mint' : view.hover.color}
            onChange={(c) => view.setHover({ color: c === 'mint' ? DEFAULT_HOVER.color : c })}
            allowNone={false}
          />
        </div>
      )}
    </div>
  );
}

export function RowsSection({ view, defs }) {
  const l = view.look;
  const sortable = defs.filter((d) => d.sortable);
  const sort = view.sort || view.defaultSort || {};
  return (
    <div>
      <SectionHead
        title="Rows & totals"
        hint="Where the totals show, the header, and the order the rows open in."
        onReset={view.resetRows}
        resetLabel="Reset rows & totals"
      />
      <Row label="Totals row" hint="The sums of the money columns for the rows shown.">
        <Segmented
          label="Totals row"
          value={l.totals}
          onChange={(totals) => view.setLook({ totals })}
          options={[
            ['both', 'Top & bottom'],
            ['top', 'Top'],
            ['bottom', 'Bottom'],
            ['none', 'Hidden'],
          ]}
        />
      </Row>
      {view.grouping && (
        <Row label="Group by main project" hint="Sub-projects and PO lines sit under their main project, with its totals (Sage-style).">
          <Switch on={l.group !== false} onChange={(group) => view.setLook({ group })} label="Group by main project" />
        </Row>
      )}
      <Row label="Keep the header in view" hint="The column names (and the top totals) stay put while the rows scroll.">
        <Switch on={l.sticky} onChange={(sticky) => view.setLook({ sticky })} label="Keep the header in view" />
      </Row>
      <Row label="Sort rows by" hint="Saved with the view - a header click still re-sorts.">
        <span className="flex gap-2 items-center">
          <select
            aria-label="Sort rows by"
            className={SELECT}
            value={sort.col || ''}
            onChange={(e) => view.setSort({ col: e.target.value, dir: sort.dir || 'desc' })}
          >
            {sortable.map((d) => (
              <option key={d.col} value={d.col}>
                {d.label}
              </option>
            ))}
          </select>
          <Segmented
            label="Sort direction"
            value={sort.dir || 'desc'}
            onChange={(dir) => view.setSort({ col: sort.col, dir })}
            options={[
              ['asc', '↑ A-Z'],
              ['desc', '↓ Z-A'],
            ]}
          />
        </span>
      </Row>
      <p className="text-[11px] text-text-muted mt-3 mb-0">These tables show every row that matches - there is no paging to set.</p>
    </div>
  );
}

export function SharingSection({ view, onShare }) {
  const a = view.active;
  return (
    <div>
      <SectionHead
        title="Sharing"
        hint="Share one of your views with people - it shows up in their views menu, read only, always your latest version."
      />
      {!a && (
        <p className="text-[12px] m-0">
          You are on the built-in <b>Default</b>. Save your changes as a view (below) to share it.
        </p>
      )}
      {a && a.mine === false && (
        <div className="space-y-2 text-[12px]">
          <p className="m-0">
            <b>{a.name}</b> is shared with you by <b>{a.ownerName}</b> - read only.
          </p>
          <Button size="sm" variant="secondary" onClick={() => view.copy(a.id)}>
            Save a copy as mine
          </Button>
        </div>
      )}
      {a && a.mine !== false && (
        <div className="space-y-2 text-[12px]">
          <p className="m-0" data-testid="sharing-state">
            {(a.sharedWith || []).length ? (
              <>
                <b>{a.name}</b> is shared with{' '}
                {a.sharedWith.includes('*') ? (
                  <b>everyone</b>
                ) : (
                  <b>
                    {a.sharedWith.length} {a.sharedWith.length === 1 ? 'person' : 'people'}
                  </b>
                )}
                .
              </>
            ) : (
              <>
                <b>{a.name}</b> is not shared.
              </>
            )}
          </p>
          <Button size="sm" onClick={() => onShare(a)}>
            Share…
          </Button>
        </div>
      )}
    </div>
  );
}
